import { FC, useCallback, useEffect, useRef } from 'react';

import { useCameraPreview } from '../../../hooks/useCameraPreview';
import { Button, Spinner, Pressable } from '../../../shared/ui';
import { VideoIcon } from '../../icons/video';

export interface GroupClientLobbyProps {
  /** Психолог уже разрешил вход — показываем кнопку «Войти» и автоматически запускаем вход */
  connectionAllowed: boolean;
  /** Состояние камеры с экрана «Запросить разрешение» */
  initialCameraEnabled?: boolean;
  onEnterMeeting: (isCameraEnabled: boolean) => void;
}

/**
 * Экран ожидания для клиента групповой терапии: после «Запросить разрешение»
 * клиент видит этот экран до разрешения психолога; после разрешения — автоматический вход на встречу (или кнопка «Войти»).
 */
export const GroupClientLobby: FC<GroupClientLobbyProps> = ({
  connectionAllowed,
  initialCameraEnabled = true,
  onEnterMeeting,
}) => {
  const wasAllowedRef = useRef(false);
  const { videoRef, isLoading, cameraError, isCameraEnabled, toggleCamera, stopStream } =
    useCameraPreview(initialCameraEnabled);
  const isCameraEnabledRef = useRef(isCameraEnabled);
  isCameraEnabledRef.current = isCameraEnabled;

  const enterMeeting = useCallback(() => {
    stopStream();
    onEnterMeeting(isCameraEnabledRef.current);
  }, [onEnterMeeting, stopStream]);

  useEffect(() => {
    if (connectionAllowed && !wasAllowedRef.current) {
      wasAllowedRef.current = true;
      enterMeeting();
    }
    if (!connectionAllowed) {
      wasAllowedRef.current = false;
    }
  }, [connectionAllowed, enterMeeting]);

  return (
    <div className="fixed inset-0 w-screen h-screen min-w-full min-h-full flex flex-col bg-ink">
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-[344px] sm:max-w-[390px] mb-6 rounded-card overflow-hidden bg-ink relative aspect-video shadow-raised">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover -scale-x-100"
          />

          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-ink">
              <Spinner size="lg" className="text-white" />
            </div>
          )}

          {cameraError && !isLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-ink text-white text-center px-4 gap-2">
              <VideoIcon checked width={40} height={40} />
              <p className="text-ui-xs leading-relaxed">{cameraError}</p>
            </div>
          )}

          {!isCameraEnabled && !cameraError && !isLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-ink text-white gap-2">
              <VideoIcon checked width={40} height={40} />
              <p className="text-ui-xs">Камера выключена</p>
            </div>
          )}

          <div className="absolute bottom-2 left-2 flex gap-2">
            <Pressable
              aria-label={isCameraEnabled ? 'Выключить камеру' : 'Включить камеру'}
              aria-pressed={isCameraEnabled}
              disabled={!!cameraError}
              className="rounded-full disabled:opacity-40"
              onClick={toggleCamera}
            >
              <VideoIcon checked={!isCameraEnabled} width={36} height={36} />
            </Pressable>
          </div>
        </div>

        {!connectionAllowed ? (
          <>
            <Spinner size="xl" className="mb-6 text-white" />
            <p className="text-white text-center text-heading-md mb-2">Подключение к встрече</p>
            <p className="text-white text-center text-ui ">Подождите, пока организатор примет ваш запрос</p>
          </>
        ) : (
          <>
            <p className="text-white text-center text-body-xl mb-6">Психолог разрешил вам войти на встречу.</p>
            <Button size="lg" className="w-[344px] max-w-[90%] sm:w-[390px]" onClick={enterMeeting}>
              Войти
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
